/* 시공사례 등록 — 템플릿 고르기(카테고리·스타일·검색). 만든 시공사례 보기는 "나의 시공사례"(CasesPage) */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Button, EmptyState, FilterChip, Page, PageHeader, Search, Temp_TileButton } from '@indipia/ui-core';
import { STYLES } from '../model/constants';
import { CAT_LABEL, TEMPLATES, previewPages, type Template } from '../model/templates';
import { useEd } from '../store/editor';
import { photoState, refreshSitesIfEmpty } from '../store/photos';
import { NoPhotos } from './Common';
import { PageThumb } from './render';
import { TemplateModal } from './TemplateModal';
import { CategoryTabs, type CategoryTab } from './CategoryTabs';
import { CARD, CARD_THUMB as THUMB, CARD_GRID as GRID, chipRow, cx } from './ui';

const SVG = (d: ReactNode) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{d}</svg>;
const HOME_CATS: CategoryTab[] = [
  { value: 'all', label: '전체', icon: SVG(<><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="7" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /><rect x="13" y="13" width="7" height="7" rx="1.5" /></>) },
  { value: 'whole', label: '전체시공', icon: SVG(<><path d="M4 11 12 4l8 7" /><path d="M6 10v10h12V10" /><path d="M10 20v-5h4v5" /></>) },
  { value: 'kitchen', label: '주방', icon: SVG(<><rect x="4" y="9" width="16" height="11" rx="1.5" /><path d="M4 13h16M9 9V5M15 9V5M12 16h0" /></>) },
  { value: 'bath', label: '욕실', icon: SVG(<><path d="M4 12h16v3a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5v-3Z" /><path d="M7 12V6a2 2 0 0 1 4 0" /><path d="M8 20l-1 1.5M16 20l1 1.5" /></>) },
  { value: 'style', label: '스타일', icon: SVG(<><path d="M12 4a8 8 0 1 0 0 16c1.1 0 1.5-.8 1.2-1.7-.4-1 .2-2 1.3-2H17a3 3 0 0 0 3-3c0-5-3.6-9.3-8-9.3Z" /><circle cx="8" cy="11" r="1" /><circle cx="11" cy="7.5" r="1" /><circle cx="15.5" cy="8.5" r="1" /></>) },
];
const CAT_NOTE: Record<string, string> = { whole: '집 전체를 공간별로 소개해요', kitchen: '주방 사진만으로 구성해요', bath: '욕실 사진만으로 구성해요', style: '컨셉 무드에 맞춘 색·글꼴·톤' };

function matchQuery(t: Template, q: string) {
  const hay = [t.name, t.desc, CAT_LABEL[t.cat], t.style ? STYLES[t.style].label : '', ...t.tags].join(' ');
  return q.split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
}

/** 한 줄 격자 — 창 폭에 들어가는 개수만 그린다. 나머지는 "모두 보기"에서 */
function OneRow<T>({ items, render }: { items: T[]; render: (x: T) => ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(6);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const css = getComputedStyle(document.documentElement);
    const min = parseFloat(css.getPropertyValue('--spacing-13')) + parseFloat(css.getPropertyValue('--spacing-07'));
    const gap = parseFloat(css.getPropertyValue('--gap-md'));
    const fit = () => setCols(Math.max(1, Math.floor((el.clientWidth + gap) / (min + gap))));
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    fit();
    return () => ro.disconnect();
  }, []);
  return <div ref={ref} className={cx(GRID, 'px-01 pb-04 pt-02')}>{items.slice(0, cols).map(render)}</div>;
}

function TplCard({ t, onOpen }: { t: Template; onOpen: (t: Template) => void }) {
  const pages = previewPages(t);
  return (
    <Temp_TileButton plain className={cx(CARD, 'rounded-lg')} aria-label={`${t.name} 템플릿 미리보기`} onClick={() => onOpen(t)}>
      <span className={THUMB}>
        <PageThumb page={pages[0]} width="fill" />
        <span className="absolute bottom-03 right-03 inline-flex h-[var(--badge-height-base)] items-center rounded-sm bg-[var(--alpha-black-700)] px-03 text-caption font-medium text-[var(--gray-0)]">{pages.length}페이지</span>
      </span>
      <strong className="mx-01 mt-02 truncate text-body-sm font-semibold">{t.name}</strong>
    </Temp_TileButton>
  );
}

/** 시공사례 등록 — 템플릿 고르기. 만든 시공사례 보기는 "나의 시공사례"(CasesPage) */
export function Home({ onBlank }: { onBlank: () => void }) {
  useEd();
  const [cat, setCat] = useState('all');
  const [style, setStyle] = useState('all');
  const [q, setQ] = useState('');
  const [modalTpl, setModalTpl] = useState<Template | null>(null);
  useEffect(() => { void refreshSitesIfEmpty(); }, []);
  const pickCat = (c: string) => { setCat(c); setStyle('all'); setQ(''); };


  const sectionHead = (title: ReactNode, note: ReactNode, more?: ReactNode) => (
    <div className="mb-05 flex items-baseline gap-sm"><h2 className="m-0 text-heading-md">{title}</h2><span className="flex-1 text-body-sm text-text-secondary">{note}</span>{more}</div>
  );
  let body: ReactNode;
  if (q) {
    const hits = TEMPLATES.filter((t) => matchQuery(t, q));
    body = (
      <section className="mt-07">
        {sectionHead(`'${q}' 검색 결과`, `${hits.length}개`)}
        {hits.length ? <div className={GRID}>{hits.map((t) => <TplCard key={t.id} t={t} onOpen={setModalTpl} />)}</div>
          : <EmptyState title="맞는 템플릿이 없어요" description={'"주방", "욕실", "오가닉"처럼 검색해 보세요.'} />}
      </section>
    );
  } else if (cat === 'all') {
    body = (<>
      {(['whole', 'kitchen', 'bath', 'style'] as const).map((c) => {
        const list = TEMPLATES.filter((t) => t.cat === c);
        return (
          <section key={c} className="mt-07">
            {/* 홈(전체)에서는 카테고리마다 한 줄(창 폭만큼)만 — 나머지는 "모두 보기"에서 */}
            {sectionHead(`${CAT_LABEL[c]} 템플릿`, CAT_NOTE[c],
              <Button size="sm" variant="ghost" onClick={() => pickCat(c)}>모두 보기 ({list.length}) ›</Button>)}
            <OneRow items={list} render={(t) => <TplCard key={t.id} t={t} onOpen={setModalTpl} />} />
          </section>
        );
      })}
    </>);
  } else {
    const list = TEMPLATES.filter((t) => t.cat === cat && (cat !== 'style' || style === 'all' || t.style === style));
    const title = cat === 'style' && style !== 'all' ? `${STYLES[style].label} 스타일` : `${CAT_LABEL[cat]} 템플릿`;
    body = (
      <section className="mt-07">
        {sectionHead(title, `${list.length}개`)}
        <div className={GRID}>{list.map((t) => <TplCard key={t.id} t={t} onOpen={setModalTpl} />)}</div>
      </section>
    );
  }
  return (
    <Page layout="doc" width="full">
      <PageHeader
        title="오늘은 어떤 시공사례를 만들어 볼까요?"
        subtitle="템플릿을 고르고 현장 사진을 고르면, AI가 템플릿에 맞춰 사진을 배치하고 사장님 말투로 글을 채워드려요."
        extra={<Button variant="etc" onClick={onBlank} title="템플릿을 쓰지 않고 빈 페이지에서 직접 만들어요">빈 페이지로 시작</Button>}
      />
      <div className="mb-06 flex flex-col items-center gap-md">
        <Search size="lg" width="default" className="w-full max-w-page-md" placeholder="템플릿 검색 (예: 주방, 오가닉, 그리드)" aria-label="템플릿 검색"
          value={q} onChange={(v) => setQ(v.trim())} />
        <CategoryTabs tabs={HOME_CATS} value={q ? '' : cat} onChange={pickCat} label="템플릿 분류" />
        {cat === 'style' && !q && (
          <div className={chipRow} role="group" aria-label="스타일">
            <FilterChip active={style === 'all'} onClick={() => setStyle('all')}>
              {/* 스타일 대표색(문서 테마 데이터) 견본 */}
              <i className="inline-block size-[var(--icon-sm)] rounded-full border border-[var(--alpha-black-100)]" style={{ background: `conic-gradient(${Object.values(STYLES).map((s) => s.swatch).join(',')})` }} />전체 스타일
            </FilterChip>
            {Object.entries(STYLES).map(([k, s]) => (
              <FilterChip key={k} active={style === k} onClick={() => setStyle(k)}><i className="inline-block size-[var(--icon-sm)] rounded-full border border-[var(--alpha-black-100)]" style={{ background: s.swatch }} />{s.label}</FilterChip>
            ))}
          </div>
        )}
      </div>
      {photoState.sites && !photoState.sites.length && !photoState.mine && (
        <section className="mt-07"><NoPhotos /></section>
      )}
      {body}
      {modalTpl && <TemplateModal tpl={modalTpl} onClose={() => setModalTpl(null)} />}
    </Page>
  );
}
